import NestedSelectItem from '@components/_shared/NestedSelectItem/NestedSelectItem';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';
import { Add, Close, Remove } from '@mui/icons-material';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import createDeviceDialogSx from '../../../../../_shared/sxStyles/Device/CreateDeviceDialogSx';
import DeviceTypeField from '../DeviceTypeField';
import { CreateMultipleDevicesFormProps } from './CreateMultipleDevicesForm.types';
import useCreateMultipleDevicesForm from './useCreateMultipleDevicesForm';

const QUANTITY_FIELD_SX = {
  ...createDeviceDialogSx.textField,
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
};

const CreateMultipleDevicesForm: FC<CreateMultipleDevicesFormProps> = ({
  setOpen,
  setConfirmOpen,
  setErrorMessage,
}) => {
  const {
    control,
    deviceType,
    isCustomDeviceType,
    filteredDeviceTypes,
    isUnitControllerMode,
    parentIsController,
    showParentControllerField,
    isParentControllerDisabled,
    controllerOrParentOptions,
    parentDeviceNameMap,
    controllerOrParentNameMap,
    parentControllerIdFallback,
    resetParentFields,
    handleCancel,
    isCreateButtonEnabled,
    deviceTypeError,
    // Unit controller mode
    unitControllerQuantity,
    unitControllerEntries,
    handleUnitControllerQuantityChange,
    handleUnitControllerEntryChange,
    minQuantity,
    maxQuantity,
    // Source device mode
    cascadingLevels,
    ancestorSelections,
    handleAncestorSelect,
    parentSourceDeviceEntries,
    handleAddParentSourceDevice,
    handleRemoveParentSourceDevice,
    handleParentSourceDeviceQuantityChange,
    filteredClosestParentDevices,
    // Custom device type mode
    customControllerOptions,
    customParentDeviceOptions,
    lockedCustomParentType,
    getFullDevicePath,
    noneParent,
    // Actions
    handleCreateDevices,
    handleReset,
  } = useCreateMultipleDevicesForm({
    setOpen,
    setConfirmOpen,
    setErrorMessage,
  });

  const getParentDeviceName = useCallback(
    (deviceId: string) =>
      parentDeviceNameMap.get(deviceId) ||
      controllerOrParentNameMap.get(deviceId) ||
      deviceId,
    [parentDeviceNameMap, controllerOrParentNameMap],
  );

  // Alphanumeric sort helper for device arrays
  const sortDevicesAlphanumerically = useCallback(
    (
      devices: Array<{
        deviceId: string;
        name: string;
        type: string;
        parentSourceDeviceId: string | null;
      }>,
    ) =>
      [...devices].sort((a, b) => {
        const numA = a.deviceId.match(/_(\d+)$/);
        const numB = b.deviceId.match(/_(\d+)$/);
        if (numA && numB) {
          return parseInt(numA[1], 10) - parseInt(numB[1], 10);
        }
        return a.deviceId.localeCompare(b.deviceId);
      }),
    [],
  );

  // Group custom parent device options by type for nested dropdown
  // Level 1 devices (no parentSourceDeviceId) are shown flat under their type header.
  // Level 2 devices are nested under their parent source device within the type header.
  // NONE option is always shown first when not locked to a type.
  const groupedCustomParentDeviceItems = useMemo(() => {
    const alreadySelected = new Set(
      parentSourceDeviceEntries.map((e) => e.parentSourceDeviceId),
    );
    const isNoneLocked = lockedCustomParentType === noneParent;
    const available = customParentDeviceOptions.filter(
      (d) => !alreadySelected.has(d.deviceId),
    );

    const items: JSX.Element[] = [];

    // Add NONE option when not locked to any type (or locked to NONE)
    if (!lockedCustomParentType && !alreadySelected.has(noneParent)) {
      items.push(
        <MenuItem
          key={noneParent}
          value={noneParent}
          onClick={(e) => {
            e.stopPropagation();
            handleAddParentSourceDevice(noneParent);
          }}
        >
          NONE
        </MenuItem>,
      );
    }

    // If locked to NONE, only show NONE (already added above or already selected)
    if (isNoneLocked) {
      return items;
    }

    // Group by type
    const grouped = new Map<
      string,
      Array<{
        deviceId: string;
        name: string;
        type: string;
        parentSourceDeviceId: string | null;
      }>
    >();
    for (const d of available) {
      const list = grouped.get(d.type) ?? [];
      list.push(d);
      grouped.set(d.type, list);
    }
    const types = [...grouped.keys()].sort((a, b) => a.localeCompare(b));

    // If locked to a type or only one type, render flat or nested based on level
    if (types.length <= 1 && available.length > 0) {
      const sorted = sortDevicesAlphanumerically(available);
      const hasParentSourceDevice = sorted.some((d) => d.parentSourceDeviceId);
      if (!hasParentSourceDevice) {
        // Level 1 devices: flat list
        items.push(
          ...sorted.map((device) => (
            <MenuItem
              key={device.deviceId}
              value={device.deviceId}
              onClick={(e) => {
                e.stopPropagation();
                handleAddParentSourceDevice(device.deviceId);
              }}
            >
              {device.name || device.deviceId}
            </MenuItem>
          )),
        );
        return items;
      }
      // Level 2 devices: nest under parent source device
      const byParent = new Map<
        string,
        Array<{
          deviceId: string;
          name: string;
          type: string;
          parentSourceDeviceId: string | null;
        }>
      >();
      for (const d of sorted) {
        const parentKey = d.parentSourceDeviceId || '__unset__';
        const list = byParent.get(parentKey) ?? [];
        list.push(d);
        byParent.set(parentKey, list);
      }
      const sortedParentKeys = [...byParent.keys()].sort((a, b) => {
        const numA = a.match(/_(\d+)$/);
        const numB = b.match(/_(\d+)$/);
        if (numA && numB) {
          return parseInt(numA[1], 10) - parseInt(numB[1], 10);
        }
        return a.localeCompare(b);
      });
      items.push(
        ...sortedParentKeys.map((parentId) => {
          const devices = byParent.get(parentId) ?? [];
          return (
            <NestedSelectItem
              key={parentId}
              label={getParentDeviceName(parentId)}
            >
              {sortDevicesAlphanumerically(devices).map((device) => (
                <MenuItem
                  key={device.deviceId}
                  value={device.deviceId}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddParentSourceDevice(device.deviceId);
                  }}
                  sx={{ pl: 4 }}
                >
                  {device.name || device.deviceId}
                </MenuItem>
              ))}
            </NestedSelectItem>
          );
        }),
      );
      return items;
    }

    // Multiple types: nest under collapsible type headers
    // Within each type, if devices have parentSourceDeviceId (level 2+), sub-nest under parent
    items.push(
      ...types.map((type) => {
        const devicesOfType = sortDevicesAlphanumerically(
          grouped.get(type) ?? [],
        );
        const hasParentSourceDevice = devicesOfType.some(
          (d) => d.parentSourceDeviceId,
        );

        if (!hasParentSourceDevice) {
          // Level 1 type: flat devices under type header
          return (
            <NestedSelectItem key={type} label={type.toUpperCase()}>
              {devicesOfType.map((device) => (
                <MenuItem
                  key={device.deviceId}
                  value={device.deviceId}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddParentSourceDevice(device.deviceId);
                  }}
                  sx={{ pl: 4 }}
                >
                  {device.name || device.deviceId}
                </MenuItem>
              ))}
            </NestedSelectItem>
          );
        }

        // Level 2+ type: group by parentSourceDeviceId and nest
        const byParent = new Map<
          string,
          Array<{
            deviceId: string;
            name: string;
            type: string;
            parentSourceDeviceId: string | null;
          }>
        >();
        for (const d of devicesOfType) {
          const parentKey = d.parentSourceDeviceId || '__unset__';
          const list = byParent.get(parentKey) ?? [];
          list.push(d);
          byParent.set(parentKey, list);
        }
        const sortedParentKeys = [...byParent.keys()].sort((a, b) => {
          const numA = a.match(/_(\d+)$/);
          const numB = b.match(/_(\d+)$/);
          if (numA && numB) {
            return parseInt(numA[1], 10) - parseInt(numB[1], 10);
          }
          return a.localeCompare(b);
        });
        return (
          <NestedSelectItem key={type} label={type.toUpperCase()}>
            {sortedParentKeys.map((parentId) => {
              const devices = byParent.get(parentId) ?? [];
              return (
                <NestedSelectItem
                  key={parentId}
                  label={getParentDeviceName(parentId)}
                  depth={1}
                >
                  {sortDevicesAlphanumerically(devices).map((device) => (
                    <MenuItem
                      key={device.deviceId}
                      value={device.deviceId}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddParentSourceDevice(device.deviceId);
                      }}
                      sx={{ pl: 8 }}
                    >
                      {device.name || device.deviceId}
                    </MenuItem>
                  ))}
                </NestedSelectItem>
              );
            })}
          </NestedSelectItem>
        );
      }),
    );
    return items;
  }, [
    customParentDeviceOptions,
    parentSourceDeviceEntries,
    lockedCustomParentType,
    noneParent,
    handleAddParentSourceDevice,
    getParentDeviceName,
    sortDevicesAlphanumerically,
  ]);

  return (
    <>
      <DialogContent sx={createDeviceDialogSx.dialogContent}>
        <Stack gap={2}>
          <DeviceTypeField
            control={control as any}
            filteredDeviceTypes={filteredDeviceTypes}
            onTypeChange={resetParentFields}
          />

          {deviceTypeError && (
            <Typography variant='caption' color='error'>
              {deviceTypeError}
            </Typography>
          )}

          {deviceType && isCustomDeviceType ? (
            <>
              {/* Custom device type: controller dropdown */}
              <div>
                <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                  <FormattedAlias id='Parent Controller' />
                </FormLabel>
                <Controller
                  name='parentControllerId'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      fullWidth
                      size='small'
                      displayEmpty
                      sx={createDeviceDialogSx.textField}
                    >
                      <MenuItem value='' disabled>
                        <Typography variant='body2' color='text.secondary'>
                          Select controller
                        </Typography>
                      </MenuItem>
                      {customControllerOptions.map((d) => (
                        <MenuItem key={d.deviceId} value={d.deviceId}>
                          {d.name || d.deviceId}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </div>

              {/* Custom device type: parent source device multi-select + quantity (nested by type) */}
              {(customParentDeviceOptions.length > 0 ||
                parentSourceDeviceEntries.length > 0 ||
                !!parentControllerIdFallback) && (
                <div>
                  <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                    Parent Source Devices
                    {lockedCustomParentType &&
                    lockedCustomParentType !== noneParent
                      ? ` (${lockedCustomParentType})`
                      : ''}
                  </FormLabel>
                  <Select
                    fullWidth
                    size='small'
                    value=''
                    onChange={() => {}}
                    displayEmpty
                    sx={createDeviceDialogSx.textField}
                    renderValue={() => (
                      <Typography variant='body2' color='text.secondary'>
                        Add parent source device
                      </Typography>
                    )}
                  >
                    {groupedCustomParentDeviceItems}
                  </Select>

                  {parentSourceDeviceEntries.map((entry) => (
                    <Stack
                      key={entry.parentSourceDeviceId}
                      direction='row'
                      alignItems='center'
                      gap={1}
                      sx={{
                        mt: 1,
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getFullDevicePath(entry.parentSourceDeviceId)}
                      </Typography>
                      <IconButton
                        size='small'
                        onClick={() =>
                          handleParentSourceDeviceQuantityChange(
                            entry.parentSourceDeviceId,
                            entry.quantity - 1,
                          )
                        }
                        disabled={entry.quantity <= minQuantity}
                      >
                        <Remove fontSize='small' />
                      </IconButton>
                      <TextField
                        type='number'
                        value={entry.quantity}
                        onChange={(e) =>
                          handleParentSourceDeviceQuantityChange(
                            entry.parentSourceDeviceId,
                            parseInt(e.target.value, 10) || 1,
                          )
                        }
                        size='small'
                        inputProps={{
                          min: minQuantity,
                          max: maxQuantity,
                          step: 1,
                          style: { textAlign: 'center' },
                        }}
                        sx={{ ...QUANTITY_FIELD_SX, width: 60 }}
                      />
                      <IconButton
                        size='small'
                        onClick={() =>
                          handleParentSourceDeviceQuantityChange(
                            entry.parentSourceDeviceId,
                            entry.quantity + 1,
                          )
                        }
                        disabled={entry.quantity >= maxQuantity}
                      >
                        <Add fontSize='small' />
                      </IconButton>
                      <IconButton
                        size='small'
                        onClick={() =>
                          handleRemoveParentSourceDevice(
                            entry.parentSourceDeviceId,
                          )
                        }
                      >
                        <Close fontSize='small' />
                      </IconButton>
                    </Stack>
                  ))}
                </div>
              )}

              {/* Name prefix for custom device type */}
              <div>
                <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                  Name Prefix
                </FormLabel>
                <Controller
                  name='namePrefix'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size='small'
                      placeholder='e.g. BMS_'
                      sx={createDeviceDialogSx.textField}
                    />
                  )}
                />
              </div>
            </>
          ) : deviceType && !isUnitControllerMode ? (
            <>
              {/* Source device mode: show parent controller only when NOT parentIsController */}
              {showParentControllerField && !parentIsController && (
                <div>
                  <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                    <FormattedAlias id='Parent Controller' />
                  </FormLabel>
                  <Controller
                    name='parentControllerId'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        fullWidth
                        size='small'
                        disabled={isParentControllerDisabled}
                        displayEmpty
                        sx={createDeviceDialogSx.textField}
                      >
                        <MenuItem value='' disabled>
                          <Typography variant='body2' color='text.secondary'>
                            Select controller
                          </Typography>
                        </MenuItem>
                        {controllerOrParentOptions.map((id) => (
                          <MenuItem key={id} value={id}>
                            {controllerOrParentNameMap.get(id) || id}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </div>
              )}

              {/* Cascading ancestor source device dropdowns */}
              {cascadingLevels
                .filter((level) => !level.isClosestParent)
                .map((level, index) => (
                  <div key={`ancestor-${level.type}-${index}`}>
                    <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                      Parent Source Device ({level.type})
                    </FormLabel>
                    <Select
                      fullWidth
                      size='small'
                      value={ancestorSelections[index] || ''}
                      onChange={(e: SelectChangeEvent) =>
                        handleAncestorSelect(index, e.target.value)
                      }
                      displayEmpty
                      sx={createDeviceDialogSx.textField}
                    >
                      <MenuItem value='' disabled>
                        <Typography variant='body2' color='text.secondary'>
                          Select {level.type}
                        </Typography>
                      </MenuItem>
                      {level.devices.map((device) => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                          {device.name || device.deviceId}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                ))}

              {/* Closest parent: multi-select with per-parent quantity rows */}
              {filteredClosestParentDevices.length > 0 && (
                <div>
                  <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                    {parentIsController
                      ? 'Parent Controllers'
                      : 'Parent Source Devices'}
                  </FormLabel>
                  <Select
                    fullWidth
                    size='small'
                    value=''
                    onChange={(e: SelectChangeEvent) => {
                      if (e.target.value) {
                        handleAddParentSourceDevice(e.target.value);
                      }
                    }}
                    displayEmpty
                    sx={createDeviceDialogSx.textField}
                  >
                    <MenuItem value='' disabled>
                      <Typography variant='body2' color='text.secondary'>
                        {parentIsController
                          ? 'Add controller'
                          : 'Add parent source device'}
                      </Typography>
                    </MenuItem>
                    {filteredClosestParentDevices
                      .filter(
                        (d) =>
                          !parentSourceDeviceEntries.some(
                            (e) => e.parentSourceDeviceId === d.deviceId,
                          ),
                      )
                      .map((device) => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                          {device.name || device.deviceId}
                        </MenuItem>
                      ))}
                  </Select>

                  {/* Selected parent entries with quantity rows */}
                  {parentSourceDeviceEntries.map((entry) => (
                    <Stack
                      key={entry.parentSourceDeviceId}
                      direction='row'
                      alignItems='center'
                      gap={1}
                      sx={{
                        mt: 1,
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getFullDevicePath(entry.parentSourceDeviceId)}
                      </Typography>
                      <IconButton
                        size='small'
                        onClick={() =>
                          handleParentSourceDeviceQuantityChange(
                            entry.parentSourceDeviceId,
                            entry.quantity - 1,
                          )
                        }
                        disabled={entry.quantity <= minQuantity}
                      >
                        <Remove fontSize='small' />
                      </IconButton>
                      <TextField
                        type='number'
                        value={entry.quantity}
                        onChange={(e) =>
                          handleParentSourceDeviceQuantityChange(
                            entry.parentSourceDeviceId,
                            parseInt(e.target.value, 10) || 1,
                          )
                        }
                        size='small'
                        inputProps={{
                          min: minQuantity,
                          max: maxQuantity,
                          step: 1,
                          style: { textAlign: 'center' },
                        }}
                        sx={{ ...QUANTITY_FIELD_SX, width: 60 }}
                      />
                      <IconButton
                        size='small'
                        onClick={() =>
                          handleParentSourceDeviceQuantityChange(
                            entry.parentSourceDeviceId,
                            entry.quantity + 1,
                          )
                        }
                        disabled={entry.quantity >= maxQuantity}
                      >
                        <Add fontSize='small' />
                      </IconButton>
                      <IconButton
                        size='small'
                        onClick={() =>
                          handleRemoveParentSourceDevice(
                            entry.parentSourceDeviceId,
                          )
                        }
                      >
                        <Close fontSize='small' />
                      </IconButton>
                    </Stack>
                  ))}
                </div>
              )}

              {/* Name prefix for source devices */}
              <div>
                <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                  Name Prefix
                </FormLabel>
                <Controller
                  name='namePrefix'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size='small'
                      placeholder='e.g. BMS_'
                      sx={createDeviceDialogSx.textField}
                    />
                  )}
                />
              </div>
            </>
          ) : deviceType ? (
            <>
              {/* Unit controller mode: quantity + grouped fields (parent controller hidden, always siteController) */}

              <Stack direction='row' alignItems='center' gap={2}>
                <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                  <FormattedAlias id='Quantity' />
                </FormLabel>
                <IconButton
                  size='small'
                  onClick={() =>
                    handleUnitControllerQuantityChange(
                      unitControllerQuantity - 1,
                    )
                  }
                  disabled={unitControllerQuantity <= minQuantity}
                >
                  <Remove fontSize='small' />
                </IconButton>
                <TextField
                  type='number'
                  value={unitControllerQuantity}
                  onChange={(e) =>
                    handleUnitControllerQuantityChange(
                      parseInt(e.target.value, 10) || 1,
                    )
                  }
                  size='small'
                  inputProps={{
                    min: minQuantity,
                    max: maxQuantity,
                    step: 1,
                    style: { textAlign: 'center' },
                  }}
                  sx={{ ...QUANTITY_FIELD_SX, width: 60 }}
                />
                <IconButton
                  size='small'
                  onClick={() =>
                    handleUnitControllerQuantityChange(
                      unitControllerQuantity + 1,
                    )
                  }
                  disabled={unitControllerQuantity >= maxQuantity}
                >
                  <Add fontSize='small' />
                </IconButton>
              </Stack>

              <Divider />

              {/* Grouped Device Name + Engineering ID fields per quantity */}
              {unitControllerEntries.map((entry, index) => (
                <Stack key={entry.id} gap={1}>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Device {index + 1}
                  </Typography>
                  <div>
                    <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                      <FormattedAlias id='Device Name' />
                    </FormLabel>
                    <TextField
                      value={entry.name}
                      onChange={(e) =>
                        handleUnitControllerEntryChange(
                          index,
                          'name',
                          e.target.value,
                        )
                      }
                      fullWidth
                      size='small'
                      sx={createDeviceDialogSx.textField}
                    />
                  </div>
                  <div>
                    <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                      <FormattedAlias id='Engineering ID' />
                    </FormLabel>
                    <TextField
                      value={entry.engineeringId}
                      onChange={(e) =>
                        handleUnitControllerEntryChange(
                          index,
                          'engineeringId',
                          e.target.value,
                        )
                      }
                      fullWidth
                      size='small'
                      sx={createDeviceDialogSx.textField}
                    />
                  </div>
                  {index < unitControllerEntries.length - 1 && <Divider />}
                </Stack>
              ))}
            </>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={createDeviceDialogSx.dialogActions}>
        <Button onClick={handleCancel}>
          <FormattedAlias id='Cancel' />
        </Button>
        <Button onClick={handleReset}>
          <FormattedAlias id='Clear' />
        </Button>
        <Button
          variant='contained'
          type='button'
          onClick={handleCreateDevices}
          disabled={!isCreateButtonEnabled}
        >
          <FormattedAlias id='Create Devices' />
        </Button>
      </DialogActions>
    </>
  );
};

CreateMultipleDevicesForm.displayName = 'CreateMultipleDevicesForm';

export default CreateMultipleDevicesForm;
